/*
 *   Copyright (c) 2021 vlinder Labs Private Limited
 *   All rights reserved.
 *   This software is proprietary of Vlinder Labs Pvt. Ltd and can not be distributed / used for any purpose outside of Vlinder Labs Pvt. Ltd without explicit written consent from the copyright owner.
 */
//@ts-nocheck
import React, { useState } from "react";
import SimpleBar from "simplebar-react";
import { useLocation } from "react-router-dom";
import { isMobile } from "react-device-detect";
import { CSSTransition } from "react-transition-group";
import { ChevronRightIcon, XIcon } from "@heroicons/react/solid";
import { LogoutIcon } from "@heroicons/react/outline";
import {
    Nav,
    Badge,
    Image,
    Button,
    Navbar,
    Collapse,
    Col
} from "react-bootstrap";
import { Link } from "react-router-dom";

import * as S from "./styles";

export interface IChildNavProps {
    link: string;
    title: string;
    badgeText?: string;
    badgeBg?: string;
    icon: any;
}

export interface INavProps {
    type: "collapsable" | "non-collapsable";
    eventKey?: string;
    link?: string;
    title: string;
    icon: any;
    badgeText?: string;
    badgeBg?: string;
    children: IChildNavProps[];
}

export interface ISidebarProps {
    brandLogo: any;
    brandTitle: string;
    brandLogoStyle?: any;
    brandLogoLink?: string;
    userName: string;
   // signOutLabel?: string;
    items: INavProps[];
    sidebarStyle?: any;
}

const isValidUrl = urlString => {
    try {
        return Boolean(new URL(urlString));
    } catch (e) {
        return false;
    }
};

export const Sidebar = (props: any & ISidebarProps = {}) => {
    const location = useLocation();
    const { pathname } = location;
    const [show, setShow] = useState(false);
    const [collapsedItems, setCollapsedItems] = useState([pathname]);
    const contracted = props.contracted ? "contracted" : "";
    const showClass = show ? "show" : "";

    const onCollapse = () => setShow(!show);
    const onMouseEnter = () => props.onMouseEnter && props.onMouseEnter();
    const onMouseLeave = () => props.onMouseLeave && props.onMouseLeave();

    const onNavItemCollapse = itemKey => {
        const isCollapsed = collapsedItems.some(item => item.includes(itemKey));
        const newCollapsedItems = isCollapsed
            ? collapsedItems.filter(item => !item.includes(itemKey))
            : [...collapsedItems, itemKey];
        setCollapsedItems(newCollapsedItems);
    };

    const events = isMobile ? {} : { onMouseEnter, onMouseLeave };

    const _getIcon = (Icon: any) => {
        let _icon: any = [];
        if (typeof Icon === "string") {
            if (isValidUrl(Icon)) {
                _icon.push(
                    <span className="sidebar-icon" key={`sidebar-icon-url`}>
                        <Image
                            src={Icon}
                            width={20}
                            height={20}
                            className="sidebar-icon svg-icon"
                        />
                    </span>
                );
            } else {
                const HeroIcon = require("@heroicons/react/solid");
                const NavIcon = HeroIcon[Icon];
                if (NavIcon) {
                    _icon.push(
                        <span
                            className="sidebar-icon"
                            key={`sidebar-icon-hero-icon-mapper`}
                        >
                            <NavIcon src={Icon} className="icon icon-xs me-2" />
                        </span>
                    );
                }
            }
            return _icon;
        }
        if (Icon) {
            _icon.push(
                <span className="sidebar-icon" key={`sidebar-icon-hero-icon`}>
                    <Icon className="icon icon-xs me-2" />
                </span>
            );
        }
        return _icon;
    };

    const CollapsableNavItem = props => {
        const { eventKey, title, icon: NavItemIcon, children = null } = props;
        const isOpened = collapsedItems.some(item => item.includes(eventKey));

        return (
            <Nav.Item>
                <Nav.Link
                    onClick={() => onNavItemCollapse(eventKey)}
                    aria-expanded={isOpened}
                    aria-controls={eventKey}
                    className="d-flex justify-content-between align-items-center"
                >
                    <span>
                        {_getIcon(NavItemIcon)}
                        <span className="sidebar-text">{title}</span>
                    </span>
                    <span className="link-arrow">
                        <ChevronRightIcon className="icon icon-sm" />
                    </span>
                </Nav.Link>
                <Collapse in={isOpened} className="multi-level">
                    <div id={eventKey}>{children}</div>
                </Collapse>
            </Nav.Item>
        );
    };

    const NavItem = props => {
        const {
            title,
            link,
            target,
            icon: NavItemIcon,
            image,
            badgeText,
            badgeBg,
            badgeColor = "white"
        } = props;
        const classNames = badgeText
            ? "d-flex align-items-center justify-content-between"
            : "";
        const navItemClassName = link === pathname ? "active" : "";

        return (
            <Nav.Item
                className={navItemClassName}
                onClick={() => setShow(false)}
            >
                <Nav.Link
                    as={Link}
                    to={link}
                    target={target}
                    className={classNames}
                >
                    <span>
                        {_getIcon(NavItemIcon)}
                        {image ? (
                            <Image
                                src={image}
                                width={20}
                                height={20}
                                className="sidebar-icon svg-icon"
                            />
                        ) : null}

                        {!show && contracted && !NavItemIcon && !image ? (
                            <span className="sidebar-text-contracted">
                                {title[0]}
                            </span>
                        ) : null}

                        <span className="sidebar-text">{title}</span>
                    </span>

                    {badgeText ? (
                        <Badge
                            pill
                            bg={badgeBg}
                            text={badgeColor}
                            className="badge-sm notification-count"
                        >
                            {badgeText}
                        </Badge>
                    ) : null}
                </Nav.Link>
            </Nav.Item>
        );
    };

    const _buildNavItems = () => {
        const _buildChildrenComps = (comp?: any) => {
            let _children: any = [];
            for (let i = 0; i < comp?.length; ++i) {
                const child = comp[i];
                _children.push(
                    <NavItem
                        key={`nav-child-${i}`}
                        title={child?.title}
                        link={child?.link}
                        icon={child?.icon}
                        badgeBg={child?.badgeBg}
                        badgeText={child?.badgeText}
                    />
                );
            }
            return _children;
        };

        let _comp: any = [];
        for (let i = 0; i < props.items?.length; ++i) {
            const _item = props.items[i];
            if (_item?.type == "collapsable") {
                _comp.push(
                    <CollapsableNavItem
                        key={`nav-item-${i}`}
                        eventKey={_item.eventKey}
                        title={_item.title}
                        icon={_item.icon}
                    >
                        {_buildChildrenComps(_item?.children)}
                    </CollapsableNavItem>
                );
            }
            if (_item?.type == "non-collapsable") {
                _comp.push(
                    <NavItem
                        key={`nav-item-${i}`}
                        title={_item?.title}
                        link={_item?.link}
                        icon={_item?.icon}
                        badgeBg={_item?.badgeBg}
                        badgeText={_item?.badgeText}
                    />
                );
            }
        }
        return _comp;
    };

    return (
        <>
            <Navbar
                as={Col}
                xs={12}
                expand={false}
                collapseOnSelect
                variant="dark"
                className="navbar-theme-primary px-4 d-lg-none"
            >
                <Navbar.Brand
                    as={Link}
                    to={props.brandLogoLink}
                    className="me-lg-5"
                >
                    <Image
                        src={props.brandLogo}
                        className="navbar-brand-dark"
                        style={props.brandLogoStyle}
                    />
                </Navbar.Brand>
                <div className="d-flex align-items-center">
                    <Navbar.Toggle as={Button} onClick={onCollapse}>
                        <span className="navbar-toggler-icon" />
                    </Navbar.Toggle>
                </div>
            </Navbar>
            <CSSTransition
                timeout={300}
                in={show}
                classNames="sidebar-transition"
            >
                <SimpleBar
                    {...events}
                    className={`${contracted} ${showClass} sidebar d-lg-block text-white collapse`}
                    style={{
                        ...S.simpleBarStyle,
                        ...(props.sidebarStyle || {})
                    }}
                >
                    <div className="sidebar-inner px-4 pt-3">
                        <div className="user-card d-flex d-md-none justify-content-between justify-content-md-center pb-4">
                            <div className="d-flex align-items-center">
                                <div className="avatar-lg me-4">
                                    <Image
                                        src={props.brandLogo}
                                        className="card-img-top rounded-circle border-white"
                                    />
                                </div>
                                {/* <div className="d-block">
                                    <h5 className="mb-3">
                                        Hi, {props.userName}
                                    </h5>
                                    <Button
                                        as={Link}
                                        variant="secondary"
                                        size="sm"
                                        to={"/sign-in"}
                                        className="d-inline-flex align-items-center"
                                    >
                                        <LogoutIcon className="icon icon-xxs me-1" />{" "}
                                        {props.signOutLabel}
                                    </Button>
                                </div> */}
                            </div>
                            <Nav.Link
                                className="collapse-close d-md-none"
                                onClick={onCollapse}
                            >
                                <XIcon className="icon icon-xs" />
                            </Nav.Link>
                        </div>
                        <Nav className="flex-column pt-3 pt-md-0">
                            {/* <NavItem
                                title={props.brandTitle}
                                link={props.brandLogoLink}
                                image={props.brandLogo}
                            /> */}
                            <S.BrandContainer>
                                <Navbar.Brand
                                    as={Link}
                                    to={props.brandLogoLink}
                                    className="me-lg-5"
                                    style={S.BrandStyle}
                                >
                                    <Image
                                        src={props.brandLogo}
                                        className="navbar-brand-dark"
                                        style={{
                                            ...(props.brandLogoStyle || {})
                                        }}
                                    />
                                    {!show && !contracted ? (
                                        <S.BrandTitle>
                                            {props.brandTitle}
                                        </S.BrandTitle>
                                    ) : null}
                                </Navbar.Brand>
                            </S.BrandContainer>
                            {_buildNavItems()}
                        </Nav>
                    </div>
                </SimpleBar>
            </CSSTransition>
        </>
    );
};

Sidebar.defaultProps = {
    brandLogoLink: "/",
    brandTitle: "",
    userName: "",
    items: []
};
